import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Drawer, Form, Tooltip } from 'antd';
import type { FormInstance } from 'antd';
import { ArrowLeftOutlined, ExclamationCircleFilled, EyeOutlined, ReadOutlined } from '@ant-design/icons';
import { FormShellContext, pathKey } from './context';
import './NetorcaForm.css';

/**
 * antd sizes every control from `controlHeight`. Raising it here — rather
 * than in CSS — keeps antd's own paddings, icon offsets and dropdown metrics
 * consistent, and confines the change to forms that opt in.
 */
const formTheme = {
    token: {
        controlHeight: 40,
        controlHeightSM: 32,
        borderRadius: 10,
        borderRadiusSM: 8,
        /* the dropdowns and pickers that open out of a field */
        borderRadiusLG: 14,
        colorBorder: '#e1e6f2',
        colorTextPlaceholder: '#9aa2bd',
        /* one focus ring, in the brand, for everything antd draws itself */
        controlOutline: 'rgba(87, 150, 204, 0.22)',
        controlOutlineWidth: 3,
        fontSize: 14,
    },
    components: {
        Form: { itemMarginBottom: 20, verticalLabelPadding: '0 0 6px' },
        Select: { optionSelectedBg: '#eef4fb', optionActiveBg: '#f3f6fc', optionSelectedFontWeight: 600 },
        Segmented: { itemSelectedBg: '#ffffff' },
    },
};

export type NetorcaFormProps = {
    form: FormInstance;
    /** also the id prefix antd gives every control — keep it unique per page */
    name: string;
    onFinish: (values: any) => void;
    initialValues?: any;
    /** anything the page wants to know when the user edits something */
    onValuesChange?: (changed: any, all: any) => void;
    /**
     * Bump this when the values on screen have just been persisted, and the
     * unsaved-changes marker clears. A page that stays put after a save has no
     * other way to say the form is no longer ahead of the server.
     */
    pristineKey?: string | number;

    eyebrow?: React.ReactNode;
    title: React.ReactNode;
    description?: React.ReactNode;
    /** where the page came from — an arrow beside the title, back to the list */
    onBack?: () => void;
    backLabel?: string;
    /** controls pinned to the right of the title */
    heroExtra?: React.ReactNode;
    /** long-form explanation, opened in a drawer instead of pushing the form down */
    help?: { title?: string; buttonLabel?: string; content: React.ReactNode };

    children: React.ReactNode;

    submitLabel?: string;
    submitting?: boolean;
    onCancel?: () => void;
    cancelLabel?: string;
    /** destructive action, kept away from the primary button */
    destructive?: React.ReactNode;
    /** no submit, every control disabled — used for non-admin viewers */
    readOnly?: boolean;
    /**
     * Why the form is read-only. `false` where something else on the page
     * already says so — a version picker sitting on the version being read,
     * say — so the page does not tell you twice.
     */
    readOnlyNotice?: string | false;
    /** label fields that are not required (default: true) */
    optionalMark?: boolean;
    /**
     * Errors the server rejected the save with — see useServerErrors. They
     * join the same summary as client-side failures so the user never has to
     * hunt for what went wrong.
     */
    serverErrors?: { name: any; errors: string[] }[];
    className?: string;
};

const errorSignature = (fields: { name: any; errors: string[] }[]) =>
    fields.map((f) => `${pathKey(f.name)}:${f.errors.join('|')}`).join('~');

const NetorcaForm = ({
    form,
    name,
    onFinish,
    initialValues,
    onValuesChange,
    pristineKey,
    eyebrow,
    title,
    description,
    onBack,
    backLabel = 'Back',
    heroExtra,
    help,
    children,
    submitLabel = 'Save',
    submitting = false,
    onCancel,
    cancelLabel = 'Cancel',
    destructive,
    readOnly = false,
    readOnlyNotice = 'You have read-only access to this record',
    optionalMark = true,
    serverErrors,
    className,
}: NetorcaFormProps) => {
    const [invalid, setInvalid] = useState<{ name: any; errors: string[] }[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    // a save that leaves the page where it is puts the form back level with
    // the server, and the marker below has to hear about it
    useEffect(() => {
        setDirty(false);
    }, [pristineKey]);

    /** field path → owning section id; a ref because it must not re-render */
    const fieldOwners = useRef(new Map<string, string>());
    const summaryRef = useRef<HTMLDivElement>(null);

    const registerField = useCallback((path: string, sectionId: string) => {
        fieldOwners.current.set(path, sectionId);
    }, []);
    const unregisterField = useCallback((path: string) => {
        fieldOwners.current.delete(path);
    }, []);

    // A rejected save is as much a reason to show the summary as a rejected
    // submit, so server errors take over the panel while they are unresolved.
    const shownErrors = serverErrors && serverErrors.length > 0 ? serverErrors : invalid;
    const showSummary = (submitted || (serverErrors?.length ?? 0) > 0) && shownErrors.length > 0;

    const serverSignature = errorSignature(serverErrors || []);
    useEffect(() => {
        if (serverSignature) window.requestAnimationFrame(() => summaryRef.current?.focus());
    }, [serverSignature]);

    const sectionErrors = useMemo(() => {
        const counts: Record<string, number> = {};
        shownErrors.forEach((field) => {
            const owner = fieldOwners.current.get(pathKey(field.name));
            if (owner) counts[owner] = (counts[owner] || 0) + 1;
        });
        return counts;
    }, [shownErrors]);

    /** move both the viewport and the keyboard focus to an invalid control */
    const focusField = (fieldName: any) => {
        form.scrollToField(fieldName, { behavior: 'smooth', block: 'center' });
        const instance = form.getFieldInstance(fieldName);
        if (instance?.focus) {
            instance.focus();
            return;
        }
        const id = `${name}_${(Array.isArray(fieldName) ? fieldName : [fieldName]).join('_')}`;
        document.getElementById(id)?.focus();
    };

    /** keep the summary in step with the user's corrections once it is shown */
    const syncErrors = () => {
        const next = form.getFieldsError().filter((f) => f.errors.length > 0);
        setInvalid((prev) => (errorSignature(prev) === errorSignature(next) ? prev : next));
    };

    return (
        <ConfigProvider theme={formTheme}>
            <div className={`nf-form${className ? ` ${className}` : ''}`}>
                <header className="nf-hero">
                    <div className="nf-hero__lead">
                        {/* the arrow alone says where it goes back to, and the
                            tooltip says it in words for anyone who asks */}
                        {onBack && (
                            <Tooltip title={backLabel}>
                                <button
                                    type="button"
                                    className="nf-back"
                                    aria-label={backLabel}
                                    onClick={onBack}
                                >
                                    <ArrowLeftOutlined aria-hidden="true" />
                                </button>
                            </Tooltip>
                        )}
                        <div>
                            {eyebrow && <div className="nf-hero__eyebrow">{eyebrow}</div>}
                            <h1 className="nf-hero__title">{title}</h1>
                            {description && <p className="nf-hero__desc">{description}</p>}
                        </div>
                    </div>
                    <div className="nf-hero__aside">
                        {readOnly && readOnlyNotice && (
                            <span className="nf-readonly">
                                <EyeOutlined aria-hidden="true" />
                                {readOnlyNotice}
                            </span>
                        )}
                        {help && (
                            <Button icon={<ReadOutlined />} onClick={() => setHelpOpen(true)}>
                                {help.buttonLabel ?? 'How it works'}
                            </Button>
                        )}
                        {heroExtra}
                    </div>
                </header>

                <FormShellContext.Provider value={{ registerField, unregisterField, sectionErrors }}>
                    <Form
                        className="nf-main"
                        form={form}
                        name={name}
                        layout="vertical"
                        colon={false}
                        scrollToFirstError={false}
                        disabled={readOnly}
                        initialValues={initialValues}
                        requiredMark={(label, { required }) => (
                            <span className="nf-label">
                                {/* the name gives way before the marks do: a long
                                    one is worth an ellipsis, "Optional" is not */}
                                <span className="nf-label__text">{label}</span>
                                {required ? (
                                    <>
                                        <span className="nf-label__req" aria-hidden="true">
                                            *
                                        </span>
                                        <span className="nf-sr">(required)</span>
                                    </>
                                ) : (
                                    optionalMark && <span className="nf-label__opt">Optional</span>
                                )}
                            </span>
                        )}
                        onValuesChange={(changed, all) => {
                            setDirty(true);
                            if (submitted) syncErrors();
                            onValuesChange?.(changed, all);
                        }}
                        onFinish={(values) => {
                            setSubmitted(false);
                            setInvalid([]);
                            onFinish(values);
                        }}
                        onFinishFailed={({ errorFields }) => {
                            setSubmitted(true);
                            setInvalid(errorFields);
                            // let the summary render before it takes focus
                            window.requestAnimationFrame(() => summaryRef.current?.focus());
                        }}
                    >
                        {showSummary && (
                            <div className="nf-summary" role="alert" tabIndex={-1} ref={summaryRef}>
                                <div className="nf-summary__title">
                                    <ExclamationCircleFilled aria-hidden="true" />
                                    {shownErrors.length === 1
                                        ? 'There is 1 problem to fix before saving'
                                        : `There are ${shownErrors.length} problems to fix before saving`}
                                </div>
                                <ul className="nf-summary__list">
                                    {shownErrors.map((field) => (
                                        <li key={pathKey(field.name)}>
                                            <button
                                                type="button"
                                                className="nf-summary__link"
                                                onClick={() => focusField(field.name)}
                                            >
                                                {field.errors[0]}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {children}

                        {(!readOnly || destructive || onCancel) && (
                            <div className="nf-actions" role="group" aria-label="Form actions">
                                {destructive}
                                {dirty && !readOnly && (
                                    <span className="nf-actions__status">
                                        <span className="nf-actions__dot" aria-hidden="true" />
                                        Unsaved changes
                                    </span>
                                )}
                                <div className="nf-actions__end">
                                    {/* leaving the page is not editing it: the way
                                        out stays open while the form is read-only */}
                                    {onCancel && (
                                        <Button disabled={false} onClick={onCancel}>
                                            {cancelLabel}
                                        </Button>
                                    )}
                                    {!readOnly && (
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={submitting}
                                            title={dirty ? 'Unsaved changes' : undefined}
                                        >
                                            {submitLabel}
                                            {/* hidden from the accessible name: the
                                                status beside the button already says
                                                this in words, and the button is still
                                                "Save changes" to anyone asking for it */}
                                            {dirty && (
                                                <span className="nf-actions__star" aria-hidden="true">
                                                    {' *'}
                                                </span>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </Form>
                </FormShellContext.Provider>

                {help && (
                    <Drawer
                        title={help.title ?? 'How it works'}
                        width={620}
                        open={helpOpen}
                        onClose={() => setHelpOpen(false)}
                    >
                        {help.content}
                    </Drawer>
                )}
            </div>
        </ConfigProvider>
    );
};

export default NetorcaForm;
